/**
 * Public events discovery page (`/events`).
 *
 * Visible to logged-out visitors. Infinite scroll via useDiscoverEvents
 * (TanStack `useInfiniteQuery`). Supports:
 *   • Range filter: today / this week / this month / all
 *   • Category filter chips
 *   • Free-text search by title (debounced)
 *   • Map / list toggle at top-right
 *
 * SEO note: the Node WebController.eventsIndex handler already emitted a
 * schema.org/ItemList JSON-LD shell for /events. React hydrates on top
 * with the interactive filters + infinite scroll — the crawler sees the
 * server-rendered list; users see the richer UI.
 */
import React, { useEffect, useMemo, useRef, useState } from 'react';
import { Helmet } from 'react-helmet-async';
import { Link, useNavigate, useParams, useSearchParams } from 'react-router-dom';
import { CalendarClock, List, MapPin, Map as MapIcon, Search, X } from 'lucide-react';

import { Button, Input, UserAvatar } from '@/components/ui';
import { useCategoryCounts, useDiscoverEvents, useNearbyEvents } from '@/lib/api';
import { useViewerGeo } from '@/lib/api/hooks/useViewerGeo';
import type { PlaceEvent, PlaceEventCategory } from '@/types/api';
import { EVENTS, pageview, track } from '@/lib/analytics';
import { EVENT_CATEGORIES, categoryMeta } from '@/pages/events/categories';
import { isEventExpired } from '@/pages/events/expiry';
import { cityStateLabel } from '@/pages/events/location';
import { EventsMap } from '@/pages/events/components/EventsMap';
import { FriendAttendeesBadge } from '@/pages/events/components/FriendAttendeesBadge';
import { AdsenseSlot } from '@/components/ads/AdsenseSlot';
import { AdSlots } from '@/lib/adSlots';

type Range = 'today' | 'week' | 'month' | 'all';
const RANGES: Array<{ slug: Range; label: string }> = [
  { slug: 'all', label: 'All upcoming' },
  { slug: 'today', label: 'Today' },
  { slug: 'week', label: 'This week' },
  { slug: 'month', label: 'This month' },
];

// All 33 countries enabled on the YAARIKUT Ticketmaster partner account.
// Order matches the server's ingestion grouping (North America → South
// America → Europe → Middle East → Africa → Oceania) so the dropdown
// scans geographically. `Intl.DisplayNames` renders each ISO code as a
// localised label; alphabetical display order can be done per-locale by
// consumers if needed.
const TM_COUNTRIES = [
    // North America
    'US', 'CA', 'MX',
    // South America
    'BR', 'CL', 'PE',
    // Europe
    'GB', 'IE', 'FR', 'DE', 'ES', 'IT', 'NL', 'BE', 'CH', 'AT',
    'SE', 'NO', 'DK', 'FI', 'PL', 'CZ', 'GR', 'CY', 'TR',
    // Middle East
    'AE', 'SA', 'IL',
    // Africa
    'ZA', 'KE', 'UG',
    // Oceania
    'AU', 'NZ',
];

/** Simple debounce hook. */
function useDebounced<T>(value: T, ms: number): T {
  const [debounced, setDebounced] = useState(value);
  useEffect(() => {
    const t = window.setTimeout(() => setDebounced(value), ms);
    return () => window.clearTimeout(t);
  }, [value, ms]);
  return debounced;
}

function formatWhen(startsAt: string): string {
  const iso = startsAt.includes('T') ? startsAt : startsAt.replace(' ', 'T') + 'Z';
  const d = new Date(iso);
  if (isNaN(d.getTime())) return startsAt;
  return new Intl.DateTimeFormat(undefined, {
    weekday: 'short',
    month: 'short',
    day: 'numeric',
    hour: 'numeric',
    minute: '2-digit',
  }).format(d);
}

/** Parse an event's starts_at string (MySQL UTC or ISO) into a Date, or
 *  null if it's unparseable. Used by the status helper below. */
function parseEventDate(s: string | null | undefined): Date | null {
  if (!s) return null;
  const iso = s.includes('T') ? (s.endsWith('Z') ? s : s + 'Z') : s.replace(' ', 'T') + 'Z';
  const d = new Date(iso);
  return isNaN(d.getTime()) ? null : d;
}

/** Compute display status for the badge on the card image.
 *  Priority: Cancelled > Expired > Today > Tomorrow. A plain future
 *  event returns `null` so the badge is skipped (the date line in the
 *  card body already tells the user when it is — a "Upcoming" chip
 *  on every card was visual noise). */
function getEventStatus(event: PlaceEvent): { label: string; cls: string } | null {
  if (event.status === 'C') return { label: 'Cancelled', cls: 'bg-red-100 text-red-700' };
  const start = parseEventDate(event.starts_at);
  // Expiry uses the shared helper so start-only events don't flip to
  // Expired at the start moment — they stay live until end-of-day on
  // the start date (see `@/pages/events/expiry`).
  if (isEventExpired(event.starts_at, event.ends_at)) {
    return { label: 'Expired', cls: 'bg-slate-200 text-slate-700' };
  }
  if (!start) return null;
  // Compare using LOCAL calendar days — users think "today" / "tomorrow"
  // in their own TZ, not UTC.
  const today = new Date(); today.setHours(0, 0, 0, 0);
  const tomorrow = new Date(today); tomorrow.setDate(today.getDate() + 1);
  const dayAfter = new Date(today); dayAfter.setDate(today.getDate() + 2);
  const startDay = new Date(start); startDay.setHours(0, 0, 0, 0);
  if (startDay.getTime() === today.getTime()) return { label: 'Today', cls: 'bg-emerald-100 text-emerald-700' };
  if (startDay.getTime() === tomorrow.getTime()) return { label: 'Tomorrow', cls: 'bg-amber-100 text-amber-700' };
  if (startDay.getTime() < dayAfter.getTime()) return { label: 'Today', cls: 'bg-emerald-100 text-emerald-700' };
  return null;
}

export interface EventCardProps { event: PlaceEvent }
export function EventCard({ event }: EventCardProps) {
  const meta = categoryMeta(event.category);
  const status = getEventStatus(event);
  // Fallback color for the placeholder image block when no cover was
  // uploaded. Uses the category's accent color (or brand blue).
  const accent = meta?.color || '#33B0F2';
  // Hide "Created by" when the event has no real owner (same guard as
  // EventDetail — system/legacy rows have user_id null or 0).
  const hasRealCreator =
    event.user_id != null && Number(event.user_id) > 0 && !!event.creator?.username;
  const creatorName =
    (event.creator?.firstname || event.creator?.username || '').trim();

  const onCardClick = () => {
    track(EVENTS.EVENT_CARD_CLICKED, {
      event_id: event.id,
      source: event.source ?? 'user',
      category: event.category ?? null,
      has_cover: !!event.cover_url,
      status: event.status,
    });
  };

  return (
    <li className="relative overflow-hidden rounded-xl border border-border bg-surface shadow-sm transition-all hover:border-brand-300 hover:shadow-md">
      <Link to={event.url} className="block" onClick={onCardClick}>
        {/* ── Image block ── always rendered. When no cover uploaded, a
            category-tinted gradient placeholder with a big emoji keeps the
            card visually anchored (blog-item style). Status + category
            overlay the top so key signals read before the title. */}
        <div
          className="relative aspect-square w-full overflow-hidden"
          style={event.cover_url ? undefined : {
            background: `linear-gradient(135deg, ${accent}30 0%, ${accent}0F 70%, transparent 100%)`,
          }}
        >
          {event.cover_url ? (
            <img src={event.cover_url} alt="" className="h-full w-full object-cover" />
          ) : null}
          {/* Attendee counts — top-left overlay. Dark translucent pill,
              same visual family as the status + category chips so the three
              overlays feel like one set. Only renders a segment when its
              count is > 0; a brand-new event with zero RSVPs stays uncluttered. */}
          {(event.counts.going > 0 || event.counts.maybe > 0) ? (
            <span className="absolute left-2 top-2 inline-flex items-center gap-1.5 rounded-full bg-black/60 px-2 py-0.5 text-[11px] font-semibold text-white shadow-sm backdrop-blur-sm">
              {event.counts.going > 0 ? (
                <span>{event.counts.going} going</span>
              ) : null}
              {event.counts.going > 0 && event.counts.maybe > 0 ? (
                <span aria-hidden className="opacity-60">·</span>
              ) : null}
              {event.counts.maybe > 0 ? (
                <span>{event.counts.maybe} maybe</span>
              ) : null}
            </span>
          ) : null}
          {/* Creator avatar — bottom-left overlay. Shown only when there's a
              real owner (user_id > 0 + resolvable creator). ring-2 white +
              shadow so the circle stands off any cover image. Hover tooltip
              carries the name since the label text is suppressed. */}
          {hasRealCreator ? (
            <div
              className="absolute bottom-2 left-2"
              title={creatorName || event.creator?.username || ''}
            >
              <UserAvatar
                src={event.creator?.image || undefined}
                alt={creatorName || event.creator?.username || ''}
                fallback={(creatorName || event.creator?.username || '?').slice(0, 1).toUpperCase()}
                className="h-7 w-7 ring-2 ring-white shadow-md"
              />
            </div>
          ) : null}
          {/* Category chip — bottom-right overlay, dark translucent pill
              with white text so it stays readable on any cover image. */}
          {meta ? (
            <span className="absolute bottom-2 right-2 inline-flex items-center gap-1 rounded-full bg-black/60 px-2 py-0.5 text-[11px] font-semibold text-white shadow-sm backdrop-blur-sm">
              <span aria-hidden>{meta.emoji}</span>
              {meta.label}
            </span>
          ) : null}
          {/* Status badge — top-right overlay. Only renders when the
              event is Cancelled/Expired/Today/Tomorrow; a plain future
              event has no badge because the card already shows the date. */}
          {status ? (
            <span className={`absolute right-2 top-2 inline-flex items-center rounded-full px-2 py-0.5 text-[11px] font-semibold shadow-sm ${status.cls}`}>
              {status.label}
            </span>
          ) : null}
        </div>
        {/* ── Body ── */}
        <div className="p-4">
          <h3
            className="line-clamp-2 font-display text-base font-semibold text-ink"
            style={{ lineHeight: '1.3rem' }}
          >
            {event.title}
          </h3>
          {/* Date. The N going / N maybe counts moved to a top-left
              overlay on the image above, so this line is just the date now. */}
          <div className="mt-1 flex items-center gap-1 text-xs text-muted-foreground">
            <CalendarClock className="h-3.5 w-3.5 shrink-0" />
            <span className="truncate">{formatWhen(event.starts_at)}</span>
          </div>
          {/* Location — venue name on line 1, "City, State" on line 2.
              The full street address stays off the card (lives on
              EventDetail) so the grid rhythm stays tidy; venue + the
              coarser city/state is enough to orient at a glance. */}
          {(() => {
            const cityState = cityStateLabel(event.city, event.state);
            if (!event.place_name && !cityState) return null;
            return (
              <div className="mt-1 flex items-start gap-1 text-xs text-muted-foreground">
                <MapPin className="mt-0.5 h-3.5 w-3.5 shrink-0" />
                <div className="min-w-0 flex-1">
                  {event.place_name ? (
                    <div className="truncate text-ink-2">{event.place_name}</div>
                  ) : null}
                  {cityState ? (
                    <div className="truncate">{cityState}</div>
                  ) : null}
                </div>
              </div>
            );
          })()}
          {event.friend_attendees && event.friend_attendees.length ? (
            <div className="mt-3 border-t border-border pt-3">
              <FriendAttendeesBadge event={event} />
            </div>
          ) : null}
        </div>
      </Link>
    </li>
  );
}

export default function EventsDiscovery() {
  const [range, setRange] = useState<Range>('all');
  // Category state is synced with the URL. `/events/sports` opens with
  // the Sports chip pre-selected; clicking a chip replaces the URL to
  // `/events/<slug>` (back to `/events` for "All categories"). Unknown
  // slugs fall through to All so a mistyped URL doesn't 404.
  const { categorySlug } = useParams<{ categorySlug?: string }>();
  const navigate = useNavigate();
  // Optional `?city=<name>` scope — populated by the city page's
  // "See all events in <X>" link. Case-insensitive on the server. Trimmed
  // + length-capped locally so a huge/garbage param doesn't fire a long
  // network request before the server rejects it.
  const [searchParams] = useSearchParams();
  const cityFromUrl = (() => {
    const raw = (searchParams.get('city') || '').trim();
    if (!raw || raw.length > 100) return null;
    return raw;
  })();
  const slugFromUrl = (() => {
    if (!categorySlug) return null;
    const hit = EVENT_CATEGORIES.find((c) => c.slug === categorySlug.toLowerCase());
    return hit ? (hit.slug as PlaceEventCategory) : null;
  })();
  const [category, setCategoryState] = useState<PlaceEventCategory | null>(slugFromUrl);
  // Keep local state in sync when the user navigates between `/events`
  // and `/events/<slug>` via browser back/forward or external link.
  useEffect(() => {
    // `react-hooks/set-state-in-effect` flags this, but syncing a
    // router location into local state is the canonical use of
    // setState-in-effect. The functional updater returns the same
    // value when the URL slug already matches, so React skips the
    // re-render — no cascade.
    // eslint-disable-next-line react-hooks/set-state-in-effect
    setCategoryState((prev) => (prev === slugFromUrl ? prev : slugFromUrl));
  }, [slugFromUrl]);
  // Chip clicks go through this wrapper so each selection pushes the URL
  // too — gives category pages shareable, SEO-friendly links.
  const setCategory = (next: PlaceEventCategory | null) => {
    setCategoryState(next);
    const target = next ? `/events/${next}` : '/events';
    navigate(target, { replace: false });
  };
  const [searchText, setSearchText] = useState('');
  const debouncedSearch = useDebounced(searchText, 300);
  const [view, setView] = useState<'list' | 'map'>('list');
  const [drawer, setDrawer] = useState<PlaceEvent | null>(null);
  // Custom date range — YYYY-MM-DD each. When either is set, the preset
  // Range radio is ignored by the server (and visually dimmed here).
  const [customFrom, setCustomFrom] = useState<string>('');
  const [customTo, setCustomTo] = useState<string>('');
  const hasCustomRange = customFrom !== '' || customTo !== '';

  // Resolve viewer's country/city from IP (+ profile fallback). When both
  // are unknown, source='none' and the page falls back to showing ALL
  // events with no city section — honours the "if no IP + no profile,
  // show all" requirement.
  const geo = useViewerGeo();
  // User can override the detected country via the dropdown. `null`
  // explicitly means "show all countries"; `undefined` means "not yet
  // overridden — use detected". Separating the two lets the detected
  // value propagate until the user interacts.
  const [countryOverride, setCountryOverride] = useState<string | null | undefined>(undefined);
  const activeCountry = countryOverride !== undefined ? countryOverride : (geo.data?.country ?? null);

  // "Any filter active" — drives the Clear Filters pill in the header and
  // the fallback button in the empty-state card. Both share one source of
  // truth so a filter clear hits the same five pieces of state everywhere.
  const hasActiveFilters =
    range !== 'all'
    || category !== null
    || countryOverride !== undefined
    || debouncedSearch !== ''
    || hasCustomRange
    || cityFromUrl !== null;
  // Separate flag for the "Events in <city>" rail. The rail CAN survive a
  // category pick (the nearby endpoint accepts `?category=`), so hiding
  // it on category alone would read as "the list disappeared". It still
  // hides when any OTHER filter is active, since the nearby query ignores
  // country / date / search and would read as stale against those. Also
  // hidden on map view — the map already surfaces every nearby event as
  // a pin, so repeating the rail above would be noise.
  const hideRail =
    view === 'map'
    || range !== 'all'
    || countryOverride !== undefined
    || debouncedSearch !== ''
    || hasCustomRange;
  function clearAllFilters() {
    setRange('all');
    setCategory(null);
    setSearchText('');
    setCountryOverride(undefined);
    setAutoFallback(null);
    setCustomFrom('');
    setCustomTo('');
    // Strip ?city=... from the URL so the Clear Filters button really
    // clears every filter, including the city-page handoff.
    if (cityFromUrl) navigate('/events', { replace: true });
    track(EVENTS.EVENT_DISCOVER_FILTERED, {
      action: 'clear_all',
      category_slug: null,
      country: null,
      range: 'all',
      has_custom_range: false,
      search: '',
    });
  }

  // Page-view on mount. The URL category slug is captured so GSC/
  // Mixpanel can split `/events/sports` vs `/events`.
  useEffect(() => {
    pageview(
      slugFromUrl ? `/events/${slugFromUrl}` : '/events',
      slugFromUrl
        ? `Events — ${slugFromUrl[0].toUpperCase() + slugFromUrl.slice(1)}`
        : 'Events',
    );
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Fire a single "Event Discover Filtered" whenever the composed
  // filter state settles. The `debouncedSearch` dep gates search
  // input (300 ms); the first render is skipped so the initial
  // page-view carries the entry state without a redundant filter
  // event right after it.
  const didMountFilter = useRef(false);
  useEffect(() => {
    if (!didMountFilter.current) {
      didMountFilter.current = true;
      return;
    }
    track(EVENTS.EVENT_DISCOVER_FILTERED, {
      action: 'change',
      category_slug: category ?? null,
      country: activeCountry ?? null,
      country_override: countryOverride === undefined ? 'auto' : countryOverride ?? 'all',
      range: hasCustomRange ? 'custom' : range,
      has_custom_range: hasCustomRange,
      custom_from: customFrom || null,
      custom_to: customTo || null,
      search: debouncedSearch || '',
      view,
    });
  }, [category, activeCountry, countryOverride, range, hasCustomRange, customFrom, customTo, debouncedSearch, view]);

  // Map view needs ALL pins up front so a user never zooms into a
  // Chandigarh / Mohali cluster that's actually loaded but hidden
  // behind pagination. Server caps limit at 500 (plenty for any
  // single-country slice today — India ≈ 290, US ≈ 3,500). List view
  // keeps the 20-per-page cursor so initial paint stays fast.
  const query = useDiscoverEvents({
    range,
    category,
    q: debouncedSearch,
    country: activeCountry,
    city: cityFromUrl,
    from: customFrom || null,
    to: customTo || null,
    limit: view === 'map' ? 500 : 20,
  });

  // Per-category event counts, scoped to the active country (so an India
  // viewer sees chips sorted by India event counts, not global). Drives
  // two chip behaviors: zero-count categories are HIDDEN (never click a
  // chip that returns nothing), and the remaining chips are sorted by
  // count DESC so the biggest buckets lead. "Other" stays last by
  // convention regardless of its count.
  const catCounts = useCategoryCounts(activeCountry);
  // Memoize the counts object so the identity is stable across
  // renders when the underlying data hasn't changed — otherwise
  // `visibleCategories` and the auto-clear effect see a new object
  // on every render and re-fire.
  const categoryCountMap = useMemo(
    () => catCounts.data?.counts ?? {},
    [catCounts.data],
  );
  const visibleCategories = useMemo(() => {
    const nonZero = EVENT_CATEGORIES.filter((c) => (categoryCountMap[c.slug] ?? 0) > 0);
    return nonZero.sort((a, b) => {
      if (a.slug === 'other') return 1;
      if (b.slug === 'other') return -1;
      return (categoryCountMap[b.slug] ?? 0) - (categoryCountMap[a.slug] ?? 0);
    });
  }, [categoryCountMap]);

  // Auto-clear the selected category when it has 0 events for the newly-
  // picked country. Prevents the empty-feed trap where a user picks
  // Music on India, switches to Saudi Arabia (which has no music events),
  // and sees an empty main grid with a Music chip they can't even see
  // in the chip row any more. We wait until the counts have loaded
  // (`catCounts.data != null`) to avoid clearing during the brief
  // between-renders window where counts are still null.
  useEffect(() => {
    if (!category) return;
    if (!catCounts.data) return;
    const count = categoryCountMap[category] ?? 0;
    // Reactive cleanup: when the external counts source confirms the
    // selected category is empty, clear it. The guards above gate
    // this to one setState per transition. `setCategory` is a stable
    // local function (not reactive data) — omitting it from the deps
    // is deliberate.
    if (count === 0) {
      track(EVENTS.EVENT_CATEGORY_AUTO_CLEARED, {
        category_slug: category,
        country: activeCountry ?? null,
      });
      // eslint-disable-next-line react-hooks/set-state-in-effect
      setCategory(null);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [category, catCounts.data, categoryCountMap]);

  // "Events in <city>" section — only when we have coords AND no filter
  // is active. If any filter is set (country/date/category/search/range),
  // we skip the query entirely because the rail would ignore those
  // filters and read as a stale list to the user.
  //
  // Two-stage radius: first try 50km (events IN or next to the user's
  // city), fall back to 200km when the 50km query lands empty. Example:
  // a Moga user sees local events first, but if Moga is empty we widen
  // to pick up Ludhiana (~65 km), Amritsar (~90 km), even Chandigarh
  // (~165 km). The wider query only fires once we KNOW the 50km one is
  // empty — avoids a wasted round trip for users whose city already has
  // inventory.
  const cityEvents = useNearbyEvents({
    lat: !hideRail && geo.data?.lat != null ? geo.data.lat : null,
    lng: !hideRail && geo.data?.lng != null ? geo.data.lng : null,
    radiusKm: 50,
    limit: 6,
    category,
  });
  const cityEmpty = !hideRail
    && !cityEvents.isLoading
    && cityEvents.data != null
    && cityEvents.data.events.length === 0;
  const widerEvents = useNearbyEvents({
    lat: cityEmpty && geo.data?.lat != null ? geo.data.lat : null,
    lng: cityEmpty && geo.data?.lng != null ? geo.data.lng : null,
    radiusKm: 200,
    limit: 6,
    category,
  });
  // Pick the first non-empty result; label the heading accordingly.
  // Memoize so array identity is stable across renders when the
  // underlying data hasn't flipped — downstream `useMemo`s (railIds)
  // key on this and would otherwise re-run each render.
  const railEvents = useMemo(() => {
    return (cityEvents.data?.events?.length ?? 0) > 0
      ? cityEvents.data!.events
      : (widerEvents.data?.events ?? []);
  }, [cityEvents.data, widerEvents.data]);
  const railIsWider = (cityEvents.data?.events?.length ?? 0) === 0
    && (widerEvents.data?.events?.length ?? 0) > 0;

  // Build the set of event ids already shown in the "Events in / near
  // <city>" rail above the main grid. We drop these from the discover
  // feed so the same event doesn't appear twice. Only populated when the
  // rail is actually visible — if filters hide the rail, the set stays
  // empty and no filtering happens below.
  const railIds = useMemo<Set<number>>(() => {
    if (hideRail) return new Set();
    return new Set(railEvents.slice(0, 6).map((e) => Number(e.id)));
  }, [hideRail, railEvents]);

  const allEvents = useMemo<PlaceEvent[]>(() => {
    if (!query.data) return [];
    const flat = query.data.pages.flatMap((p) => p.events);
    if (railIds.size === 0) return flat;
    return flat.filter((e) => !railIds.has(Number(e.id)));
  }, [query.data, railIds]);

  // Simple scroll sentinel for infinite scroll.
  useEffect(() => {
    if (view !== 'list') return;
    function onScroll() {
      if (query.isFetchingNextPage || !query.hasNextPage) return;
      const scrollTop = window.scrollY;
      const vh = window.innerHeight;
      const h = document.documentElement.scrollHeight;
      if (scrollTop + vh > h - 600) query.fetchNextPage();
    }
    window.addEventListener('scroll', onScroll);
    return () => window.removeEventListener('scroll', onScroll);
  }, [query, view]);

  const total = query.data?.pages[0]?.total ?? null;

  // Auto-fallback: if the viewer's DETECTED country has 0 events (common
  // for India visitors — Ticketmaster has no India inventory), silently
  // drop the country filter so they see the global feed instead of an
  // empty page. Only fires when: query finished (not loading), zero
  // results, no search text, AND the active country came from auto-detect
  // (not from a user override). This guards against a future manual
  // country picker — if someone deliberately selects "India" we respect
  // that intent instead of silently expanding scope.
  const [autoFallback, setAutoFallback] = useState<{ from: string } | null>(null);
  useEffect(() => {
    if (query.isLoading || query.isFetching) return;
    if (debouncedSearch) return;
    if (total !== 0) return;
    if (!activeCountry) return;
    // Only fallback when the country was AUTO-DETECTED from geo, not when
    // the user explicitly chose it via the override (dropdown).
    if (countryOverride !== undefined) return;
    // Reactive scope-widening when an auto-detected country returns
    // zero results — all guards above make this a true one-shot per
    // transition, not a per-render loop.
    track(EVENTS.EVENT_COUNTRY_AUTO_FALLBACK, {
      from: activeCountry,
      to: 'all',
    });
    setAutoFallback({ from: activeCountry });
    // eslint-disable-next-line react-hooks/set-state-in-effect
    setCountryOverride(null);
  }, [query.isLoading, query.isFetching, total, activeCountry, debouncedSearch, countryOverride]);

  // Human country name from ISO code. Falls back to the code if the
  // runtime doesn't support DisplayNames or the code isn't recognised.
  const countryName = (code: string): string => {
    try {
      const names = new Intl.DisplayNames(['en'], { type: 'region' });
      return names.of(code) || code;
    } catch { return code; }
  };

  // Sort the TM country list by localized display name so a 33-row
  // dropdown scans alphabetically (far easier to find than a geographic
  // ordering once the list is this long).
  const sortedCountries = useMemo(
    () => [...TM_COUNTRIES].sort((a, b) => countryName(a).localeCompare(countryName(b))),
    [],
  );

  return (
    <div className="mx-auto w-full max-w-6xl p-4 sm:p-6">
      {/* SEO head — category-specific title + description when the URL
          is `/events/<category>`, generic copy otherwise. Mirrors the
          server-side composition in `WebController.eventsIndex` so the
          SSR shell and the client hydration read the same. */}
      <Helmet>
        {(() => {
          const catMeta = category ? categoryMeta(category) : null;
          const catLabel = catMeta?.label || null;
          const countryName = activeCountry ? (() => {
            try { return new Intl.DisplayNames(['en'], { type: 'region' }).of(activeCountry) || activeCountry; }
            catch { return activeCountry; }
          })() : null;
          // Title shape (city wins over country when both present — the
          // user came in from `/events?city=…` for a reason, so lead with
          // the finer-grained scope and skip the country suffix):
          //   `/events`                 → "Upcoming events — Yaarikut"
          //   `/events/sports`          → "Upcoming Sports events — Yaarikut"
          //   `/events/sports` + IN     → "Upcoming Sports events in India — Yaarikut"
          //   `/events?city=Nagpur`     → "Upcoming events in Nagpur — Yaarikut"
          //   `/events/music?city=Pune` → "Upcoming Music events in Pune — Yaarikut"
          const titleParts: string[] = ['Upcoming'];
          if (catLabel) titleParts.push(catLabel);
          titleParts.push('events');
          if (cityFromUrl) titleParts.push(`in ${cityFromUrl}`);
          else if (countryName) titleParts.push(`in ${countryName}`);
          const title = `${titleParts.join(' ')} — Yaarikut`;
          const scope = cityFromUrl || countryName || '';
          const desc = catLabel
            ? `Discover upcoming ${catLabel.toLowerCase()} events${scope ? ` in ${scope}` : ''} on Yaarikut. Browse by date, see them on a map, and RSVP in one tap.`
            : scope
            ? `Discover upcoming public events in ${scope} on Yaarikut. Browse by category, filter by date, see events on a map.`
            : 'Discover upcoming public events on Yaarikut. Browse by category, filter by date, see events on a map.';
          return (
            <>
              <title>{title}</title>
              <meta name="description" content={desc} />
            </>
          );
        })()}
      </Helmet>

      <header className="mb-6 flex flex-wrap items-start justify-between gap-3">
        <div>
          <h1 className="font-display text-3xl font-semibold tracking-tight text-ink">
            Upcoming events{cityFromUrl ? ` in ${cityFromUrl}` : ''}
          </h1>
          {total != null ? (
            <div className="mt-1 flex flex-wrap items-center gap-2">
              <p className="text-sm text-muted-foreground">
                {total.toLocaleString()} public event{total === 1 ? '' : 's'}
              </p>
              {hasActiveFilters ? (
                <button
                  type="button"
                  onClick={clearAllFilters}
                  aria-label="Clear all filters"
                  className="inline-flex items-center gap-1 rounded-full border border-input bg-transparent px-2.5 py-1 text-xs font-medium text-ink-2 hover:bg-surface-2/60"
                >
                  <span aria-hidden>×</span> Clear filters
                </button>
              ) : null}
            </div>
          ) : null}
          {autoFallback ? (
            <p className="mt-2 inline-flex items-center gap-1 rounded-md bg-amber-50 px-2 py-1 text-xs text-amber-800">
              <span aria-hidden>🌍</span>
              No events in{' '}
              <span className="font-semibold">
                {countryName(autoFallback.from)}
              </span>{' '}
              yet — showing events worldwide.
            </p>
          ) : null}
        </div>
        <div className="inline-flex overflow-hidden rounded-md border border-input">
          <button
            type="button"
            onClick={() => {
              if (view !== 'list') track(EVENTS.EVENT_MAP_TOGGLED, { to: 'list' });
              setView('list');
            }}
            className={`inline-flex items-center gap-1.5 px-3 py-1.5 text-sm font-medium ${
              view === 'list' ? 'bg-brand-600 text-white' : 'bg-surface text-ink-2'
            }`}
            aria-pressed={view === 'list'}
          >
            <List className="h-4 w-4" /> List
          </button>
          <button
            type="button"
            onClick={() => {
              if (view !== 'map') track(EVENTS.EVENT_MAP_TOGGLED, { to: 'map' });
              setView('map');
            }}
            className={`inline-flex items-center gap-1.5 border-l border-input px-3 py-1.5 text-sm font-medium ${
              view === 'map' ? 'bg-brand-600 text-white' : 'bg-surface text-ink-2'
            }`}
            aria-pressed={view === 'map'}
          >
            <MapIcon className="h-4 w-4" /> Map
          </button>
        </div>
      </header>

      {/* Range + country + search */}
      <div className="mb-4 flex flex-wrap items-center gap-2">
        <div
          role="radiogroup"
          aria-label="Date range"
          aria-disabled={hasCustomRange || undefined}
          className={`inline-flex overflow-hidden rounded-md border border-input ${
            hasCustomRange ? 'opacity-40' : ''
          }`}
        >
          {RANGES.map((r, i) => (
            <button
              key={r.slug}
              type="button"
              role="radio"
              aria-checked={range === r.slug && !hasCustomRange}
              onClick={() => setRange(r.slug)}
              disabled={hasCustomRange}
              title={hasCustomRange ? 'Clear the custom date range to use these presets' : undefined}
              className={`px-3 py-1.5 text-sm ${
                i > 0 ? 'border-l border-input' : ''
              } ${range === r.slug && !hasCustomRange ? 'bg-brand-600 text-white' : 'bg-surface text-ink-2'}`}
            >
              {r.label}
            </button>
          ))}
        </div>

        {/* Custom date range — YYYY-MM-DD each. Either side may be empty;
            the server treats {from, to} as inclusive-of-both-ends. When
            set, the preset radiogroup above is disabled & dimmed. */}
        <div className="inline-flex items-center gap-1 rounded-md border border-input bg-surface px-2 py-1 text-sm">
          <span className="text-xs text-muted-foreground">From</span>
          <input
            type="date"
            value={customFrom}
            onChange={(e) => setCustomFrom(e.target.value)}
            aria-label="From date"
            className="h-7 bg-transparent text-sm text-ink-2 focus:outline-none"
          />
          <span className="text-xs text-muted-foreground">to</span>
          <input
            type="date"
            value={customTo}
            onChange={(e) => setCustomTo(e.target.value)}
            min={customFrom || undefined}
            aria-label="To date"
            className="h-7 bg-transparent text-sm text-ink-2 focus:outline-none"
          />
          {hasCustomRange ? (
            <button
              type="button"
              onClick={() => { setCustomFrom(''); setCustomTo(''); }}
              aria-label="Clear date range"
              className="ml-1 rounded px-1 text-xs text-muted-foreground hover:bg-surface-2/60"
            >
              ×
            </button>
          ) : null}
        </div>

        {/* Country picker — three states for `countryOverride`:
            "auto"   → undefined (use IP/profile detection)
            "all"    → null      (explicitly show every country)
            "XX"     → that ISO code
            Picking "auto" is the "Reset to IP country" affordance that
            follows an auto-fallback or any manual pick. */}
        <select
          aria-label="Country"
          className="h-9 rounded-md border border-input bg-surface px-2 text-sm text-ink-2 focus:outline-none focus:ring-2 focus:ring-brand-300"
          value={
            countryOverride === undefined
              ? 'auto'
              : countryOverride === null
              ? 'all'
              : countryOverride
          }
          onChange={(e) => {
            const v = e.target.value;
            // Clear the amber auto-fallback banner on any manual pick —
            // the user has taken control, so the "we widened for you"
            // message is no longer relevant.
            setAutoFallback(null);
            if (v === 'auto') setCountryOverride(undefined);
            else if (v === 'all') setCountryOverride(null);
            else setCountryOverride(v);
          }}
        >
          <option value="auto">
            Auto{geo.data?.country ? ` · ${countryName(geo.data.country)}` : ''}
          </option>
          <option value="all">All countries</option>
          {sortedCountries.map((c) => (
            <option key={c} value={c}>{countryName(c)}</option>
          ))}
        </select>

        <div className="relative flex-1 min-w-[200px]">
          <Search className="pointer-events-none absolute left-2.5 top-1/2 h-4 w-4 -translate-y-1/2 text-muted-foreground" aria-hidden />
          <Input
            value={searchText}
            onChange={(e) => setSearchText(e.target.value)}
            placeholder="Search by title or location…"
            className="pl-9"
            aria-label="Search events"
          />
        </div>
      </div>

      {/* Category chips */}
      <div role="radiogroup" aria-label="Category" className="mb-6 flex flex-wrap gap-2">
        <button
          type="button"
          role="radio"
          aria-checked={category === null}
          onClick={() => setCategory(null)}
          className={`inline-flex items-center gap-1 rounded-full border px-3 py-1 text-sm ${
            category === null
              ? 'bg-brand-600 border-brand-600 text-white'
              : 'border-input bg-surface text-ink-2 hover:bg-surface-2/60'
          }`}
        >
          All categories
        </button>
        {visibleCategories.map((c) => {
          const active = category === c.slug;
          const n = categoryCountMap[c.slug] ?? 0;
          return (
            <button
              key={c.slug}
              type="button"
              role="radio"
              aria-checked={active}
              onClick={() => setCategory(active ? null : c.slug)}
              className={`inline-flex items-center gap-1 rounded-full border px-3 py-1 text-sm ${
                active
                  ? 'bg-brand-600 border-brand-600 text-white'
                  : 'border-input bg-surface text-ink-2 hover:bg-surface-2/60'
              }`}
            >
              <span aria-hidden>{c.emoji}</span>
              {c.label}
              {/* Small inventory count — mutes slightly so the label still
                  leads. Formatted so 2,816 reads clean. On active chip the
                  count sits on a translucent-white pill so it stays
                  legible against the brand-600 bg. */}
              <span
                className={`ml-0.5 rounded-full px-1 text-[11px] ${
                  active
                    ? 'bg-white/25 text-white'
                    : 'text-muted-foreground'
                }`}
              >
                {n.toLocaleString()}
              </span>
            </button>
          );
        })}
      </div>

      {/* ── "Events in <city>" rail ── shows up to 6 nearby events when
          we have resolved coords AND no filter is active. We hide the
          rail whenever ANY filter is set (`hasActiveFilters`) because the
          rail is nearby-only (lat/lng-based) and does NOT respect
          country / category / date / search — a visible "nearby" row
          while the user has narrowed to e.g. country=Canada reads as
          "the list didn't refresh". */}
      {!hideRail
        && geo.data?.city && geo.data?.lat != null && geo.data?.lng != null
        && railEvents.length > 0 ? (
        <section className="mb-8">
          <div className="mb-3 flex items-end justify-between gap-2">
            <h2 className="font-display text-xl font-semibold text-ink">
              {(() => {
                // Compose heading. When a category is selected, mention
                // it explicitly so the rail reads coherently with the
                // chip above ("Music events in Mumbai").
                const catLabel = category ? categoryMeta(category)?.label : null;
                const prefix = railIsWider ? 'near' : 'in';
                return catLabel
                  ? `${catLabel} events ${prefix} ${geo.data.city}`
                  : `Events ${prefix} ${geo.data.city}`;
              })()}
            </h2>
            <span className="text-xs text-muted-foreground">
              {railEvents.length}{railIsWider ? ' within 200 km' : ' nearby'}
            </span>
          </div>
          <ul className="grid grid-cols-2 gap-3 md:grid-cols-3 min-[900px]:grid-cols-4">
            {railEvents.slice(0, 6).map((e) => (
              <EventCard key={e.id} event={e} />
            ))}
          </ul>
        </section>
      ) : null}

      {query.isLoading ? (
        <p className="text-muted-foreground">Loading events…</p>
      ) : query.error ? (
        <p className="text-red-500">Could not load events.</p>
      ) : view === 'map' ? (
        <>
          <EventsMap events={allEvents} onSelect={setDrawer} />
          {/* Simple side-drawer / bottom-sheet; the Dialog pattern would
              be overkill — one <aside> handles both desktop and mobile
              via a responsive position switch. */}
          {drawer ? (
            <aside
              /* Mobile: bottom-sheet. Desktop: floating card anchored
                 right, offset below the site nav (top-20) and off the
                 edges (right-4, bottom-4) so it doesn't hide behind the
                 sticky header or get flush-cropped to the viewport. */
              className="fixed inset-x-0 bottom-0 z-30 max-h-[75vh] overflow-auto rounded-t-2xl border-t border-border bg-surface p-4 shadow-xl md:inset-x-auto md:bottom-4 md:left-auto md:right-4 md:top-20 md:max-h-[calc(100vh-6rem)] md:w-[380px] md:rounded-2xl md:border"
              role="dialog"
              aria-label="Event preview"
            >
              <div className="flex items-start justify-between gap-2">
                <h2 className="min-w-0 flex-1 pr-2 font-display text-lg font-semibold leading-tight">{drawer.title}</h2>
                <button
                  type="button"
                  onClick={() => setDrawer(null)}
                  className="shrink-0 rounded-md p-1 text-muted-foreground hover:bg-surface-2 hover:text-ink"
                  aria-label="Close preview"
                >
                  <X className="h-4 w-4" />
                </button>
              </div>
              {/* Cover image — shows the FULL image (no crop) using
                  `object-contain`, capped at 60vh so a tall portrait
                  (Skillboxes often ships 1080×1350) doesn't swallow the
                  whole drawer. Container has a neutral background so the
                  letterbox bars (unavoidable with contain) read as a
                  frame rather than empty space. Placeholder path keeps
                  the old 16:8 shape since there's no natural dimension
                  for the gradient. */}
              {(() => {
                const meta = categoryMeta(drawer.category);
                const accent = meta?.color || '#33B0F2';
                return (
                  <div className="relative mt-3 overflow-hidden rounded-lg bg-surface-2">
                    {drawer.cover_url ? (
                      <img
                        src={drawer.cover_url}
                        alt=""
                        className="mx-auto block max-h-[60vh] w-full object-contain"
                      />
                    ) : (
                      <div
                        className="flex aspect-[16/8] w-full items-center justify-center"
                        style={{
                          background: `linear-gradient(135deg, ${accent}30 0%, ${accent}0F 70%, transparent 100%)`,
                        }}
                      >
                        <span className="text-4xl opacity-60" aria-hidden>
                          {meta?.emoji ?? '📅'}
                        </span>
                      </div>
                    )}
                    {meta ? (
                      <span className="absolute bottom-2 right-2 inline-flex items-center gap-1 rounded-full bg-black/60 px-2 py-0.5 text-[11px] font-semibold text-white shadow-sm backdrop-blur-sm">
                        <span aria-hidden>{meta.emoji}</span>
                        {meta.label}
                      </span>
                    ) : null}
                  </div>
                );
              })()}
              <div className="mt-3 flex items-center gap-1 text-sm text-muted-foreground">
                <CalendarClock className="h-3.5 w-3.5" /> {formatWhen(drawer.starts_at)}
              </div>
              {drawer.place_name ? (
                <div className="mt-0.5 flex items-center gap-1 text-sm text-muted-foreground">
                  <MapPin className="h-3.5 w-3.5" /> {drawer.place_name}
                </div>
              ) : null}
              {drawer.description ? (
                <div
                  className="prose prose-sm mt-3 line-clamp-5 max-w-none text-ink"
                  dangerouslySetInnerHTML={{ __html: drawer.description }}
                />
              ) : null}
              <div className="mt-4">
                <Button asChild size="sm">
                  <Link to={drawer.url}>Open event →</Link>
                </Button>
              </div>
            </aside>
          ) : null}
        </>
      ) : allEvents.length === 0 ? (
        <div className="rounded-xl border border-dashed border-border p-8 text-center">
          <p className="text-muted-foreground">
            No upcoming events match your filters.
          </p>
          {hasActiveFilters ? (
            <div className="mt-4">
              <Button variant="outline" size="sm" onClick={clearAllFilters}>
                Clear filters
              </Button>
            </div>
          ) : null}
        </div>
      ) : (
        <>
          <ul className="grid grid-cols-2 gap-4 md:grid-cols-3 min-[900px]:grid-cols-4">
            {allEvents.map((e, i) => (
              <React.Fragment key={e.id}>
                <EventCard event={e} />
                {/* In-feed ad every 12 cards. Spans the full row via
                    `col-span-full`. Only renders when there's more content
                    below (not on the last partial row) so the ad never ends
                    up stranded at the bottom. Hard page cap (3) is enforced
                    by AdsenseSlot itself, so no need to count here. */}
                {(i + 1) % 12 === 0 && i + 1 < allEvents.length ? (
                  <li className="col-span-full">
                    <AdsenseSlot {...AdSlots.inFeed} className="my-0" />
                  </li>
                ) : null}
              </React.Fragment>
            ))}
          </ul>
          <div className="mt-6 flex justify-center">
            {query.isFetchingNextPage ? (
              <span className="text-sm text-muted-foreground">Loading more…</span>
            ) : query.hasNextPage ? (
              <Button variant="outline" onClick={() => query.fetchNextPage()}>
                Load more
              </Button>
            ) : (
              <span className="text-xs text-muted-foreground">End of list</span>
            )}
          </div>
          {/* Bottom banner ad — after the "Load more" / end-of-list control
              so it never pushes the pagination off-screen. */}
          <AdsenseSlot {...AdSlots.display} className="mt-6" />
        </>
      )}
    </div>
  );
}
